import React from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle, TrendingUp } from "lucide-react";
import NumericInput from "@/components/ui/NumericInput";
import type { MortgageData } from "../../CalculatorTabs";
import { AutoCalculateOptions } from "../../utils/types";
import { formatInterestRate, parseInterestRate } from "../../utils/formatters";
import { useMarketData } from "@/contexts/MarketDataContext";

interface InterestRateInputProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  onInterestRateChange: (value: number) => void;
}

const InterestRateInput: React.FC<InterestRateInputProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate,
  onInterestRateChange
}) => {
  const { marketData, isConnected } = useMarketData();
  const handleInterestRateChange = (value: number) => {
    // Clamp between 1 and 15
    const clampedValue = Math.max(1, Math.min(15, value));
    onInterestRateChange(clampedValue);
  };

  const getLoanTypeRateInfo = () => {
    switch (mortgageData.loanType) {
      case 'va':
        return {
          typical: '6.25% - 6.75%',
          note: 'VA loans often offer competitive rates due to government backing',
          benefit: 'No PMI required'
        };
      case 'fha':
        return {
          typical: '6.50% - 7.00%',
          note: 'FHA rates may be slightly higher but offset by lower down payment',
          benefit: 'Lower credit score requirements'
        };
      case 'usda':
        return {
          typical: '6.00% - 6.50%',
          note: 'USDA loans typically offer below-market rates',
          benefit: 'Government subsidized rates'
        };
      case 'conventional':
      default:
        return {
          typical: '6.50% - 7.25%',
          note: 'Rates vary based on credit score, down payment, and market conditions',
          benefit: 'Wide range of rate options'
        };
    }
  };

  const rateInfo = getLoanTypeRateInfo();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-medium">Interest Rate</Label>
        {isConnected && marketData && (
          <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
            Live Rate
          </Badge>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Typical {mortgageData.loanType.toUpperCase()} rates: {rateInfo.typical}
                </p>
                <p className="text-xs text-muted-foreground">
                  {rateInfo.note}
                </p>
                <p className="text-xs font-medium text-green-600">
                  ✓ {rateInfo.benefit}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TrendingUp className="w-4 h-4 text-muted-foreground" />
      </div>

      <NumericInput
        label=""
        value={mortgageData.interestRate}
        onChange={handleInterestRateChange}
        formatValue={formatInterestRate}
        parseValue={parseInterestRate}
        min={1}
        max={15}
        step={0.125}
        showSlider={true}
        sliderStep={0.125}
        labelClassName="text-sm font-medium"
        inputClassName="w-24 text-right"
        showMinMax={true}
      />

      {/* Rate Context Information */}
      <div className="p-2 bg-muted/20 rounded-lg border-l-4 border-blue-500">
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium">Typical {mortgageData.loanType.toUpperCase()} Range:</span>
            <span className="font-mono">{rateInfo.typical}</span>
          </div>
          <div className="text-xs">
            {rateInfo.note}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterestRateInput;
